import { useState, useEffect, useRef } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/lib/categories";
import { useLang } from "@/lib/i18n";

const activityLinks = CATEGORIES.map((c) => ({ to: `/programs/${c.key}`, label: c.label, label_hi: c.label_hi }));
const involveLinks = [
  { to: "/membership", label: "Membership" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/donate", label: "Donate" },
];

const primary = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { label: "Activities", children: activityLinks },
  { to: "/events", label: "Events" },
  { to: "/schedule", label: "Schedule" },
  { to: "/news", label: "News" },
  { to: "/gallery", label: "Gallery" },
  { label: "Get Involved", children: involveLinks },
  { to: "/transparency", label: "Transparency" },
  { to: "/contact", label: "Contact" },
];

const footerLinks = [
  { to: "/about", label: "About Us" },
  { to: "/events", label: "Events" },
  { to: "/news", label: "News" },
  { to: "/gallery", label: "Gallery" },
  { to: "/membership", label: "Membership" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/donate", label: "Donate" },
  { to: "/transparency", label: "Transparency" },
  { to: "/contact", label: "Contact" },
  { to: "/admin/login", label: "Admin Login" },
];

function LangSwitch({ className = "" }) {
  const { lang, setLang } = useLang();
  const btn = (l, text, short) => (
    <button type="button" onClick={() => setLang(l)} aria-pressed={lang === l} aria-label={text} className={`px-2 sm:px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${lang === l ? "bg-[#194324] text-white" : "text-[#194324] hover:bg-[#E8F0EA]"}`} data-testid={`lang-switch-${l}`}><span className="hidden sm:inline">{text}</span><span className="sm:hidden">{short}</span></button>
  );
  return <div className={`flex items-center gap-0.5 rounded-full border border-[#E5D298] bg-white/70 p-0.5 ${className}`} role="group" aria-label="Language">{btn("hi", "हिन्दी", "हिं")}<span className="text-[#B38728] text-xs">|</span>{btn("en", "English", "EN")}</div>;
}

function Dropdown({ label, children }) {
  const { t, tr } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="px-2 2xl:px-3 py-2 rounded-full text-sm font-medium text-[#3A4E41] hover:text-[#194324] hover:bg-[#F2EEE5] flex items-center gap-1 whitespace-nowrap" data-testid={`nav-dropdown-${label.replace(/\s/g, "-").toLowerCase()}`}>
        {t(label)} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 w-56 z-50">
          <div className="bg-white rounded-xl border border-[#E2DAD0] shadow-lg py-2">
            {children.map((c) => (
              <NavLink key={c.to} to={c.to} className="block px-4 py-2.5 text-sm text-[#3A4E41] hover:bg-[#F2EEE5] hover:text-[#194324]">{c.label_hi !== undefined ? tr(c, "label") : t(c.label)}</NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PublicLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t, tr } = useLang();

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F3]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FAF8F3]/90 border-b border-[#E5D298]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 gap-2 sm:gap-4">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 xl:shrink-0" data-testid="brand-logo">
              <img src="/logo-full-160.png" srcSet="/logo-full-160.png 1x, /logo-full-320.png 2x" alt="Hatha Vidya Charitable Trust logo" width="172" height="160" className="h-12 sm:h-14 w-auto shrink-0 object-contain" data-testid="header-logo-img" />
              <div className="leading-tight whitespace-nowrap">
                <div className="font-cinzel font-bold text-[#194324] text-sm sm:text-base tracking-wide">HATHA VIDYA</div>
                <div className="hidden min-[400px]:block xl:hidden min-[1600px]:block text-[9px] sm:text-[10px] text-[#B38728] font-mono uppercase tracking-[0.18em]">Charitable Trust · Gandhidham</div>
              </div>
            </Link>

            <nav className="hidden xl:flex items-center gap-0 2xl:gap-0.5">
              {primary.map((l) => l.children
                ? <Dropdown key={l.label} label={l.label} children={l.children} />
                : <NavLink key={l.to} to={l.to} className={({ isActive }) => `px-2 2xl:px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${isActive ? "text-[#194324] bg-[#E8F0EA]" : "text-[#3A4E41] hover:text-[#194324] hover:bg-[#F2EEE5]"}`}>{t(l.label)}</NavLink>
              )}
            </nav>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <LangSwitch />
              <Link to="/donate" className="hidden sm:block"><Button className="rounded-full bg-[#C59B27] hover:bg-[#A8811A] text-white font-semibold shadow-md active:scale-[0.98]" data-testid="donate-cta">{t("Donate")}</Button></Link>
              <button className="xl:hidden p-2 text-[#194324]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
            </div>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-[#E5D298]/50 bg-[#FAF8F3] px-4 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
            <MobileLink to="/" label={t("Home")} />
            <MobileLink to="/about" label={t("About Us")} />
            <div className="pt-2 text-xs font-mono uppercase tracking-widest text-[#B38728] px-4">{t("Activities")}</div>
            {activityLinks.map((l) => <MobileLink key={l.to} to={l.to} label={tr(l, "label")} />)}
            <MobileLink to="/events" label={t("Events")} />
            <MobileLink to="/schedule" label={t("Ashram Schedule")} />
            <MobileLink to="/news" label={t("News & Announcements")} />
            <MobileLink to="/gallery" label={t("Gallery")} />
            <div className="pt-2 text-xs font-mono uppercase tracking-widest text-[#B38728] px-4">{t("Get Involved")}</div>
            {involveLinks.map((l) => <MobileLink key={l.to} to={l.to} label={t(l.label)} />)}
            <MobileLink to="/transparency" label={t("Transparency")} />
            <MobileLink to="/contact" label={t("Contact")} />
            <Link to="/admin/login" className="block pt-2"><Button variant="outline" className="w-full">{t("Admin Login")}</Button></Link>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="bg-gradient-to-b from-[#0E2916] to-[#081B0E] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <img src="/logo-full-320.png" alt="Hatha Vidya Charitable Trust logo" width="344" height="320" className="h-24 w-auto object-contain mb-4" loading="lazy" data-testid="footer-logo-img" />
            <div className="font-cinzel font-bold text-lg text-[#C59B27] mb-3">HATHA VIDYA CHARITABLE TRUST</div>
            <p className="text-sm text-white/70 leading-relaxed">{t("A spiritual and humanitarian trust dedicated to yoga, service and community welfare in Gandhidham–Kutch, Gujarat.")}</p>
          </div>
          <div>
            <div className="font-semibold text-[#C59B27] mb-3 uppercase text-xs tracking-[0.18em] font-mono">{t("Explore")}</div>
            <ul className="space-y-2 text-sm text-white/70">
              {footerLinks.map((l) => <li key={l.to}><Link to={l.to} className="hover:text-[#C59B27] transition-colors">{t(l.label)}</Link></li>)}
            </ul>
          </div>
          <div>
            <div className="font-semibold text-[#C59B27] mb-3 uppercase text-xs tracking-[0.18em] font-mono">{t("Ashram")}</div>
            <p className="text-sm text-white/70 leading-relaxed">{t("Gandhidham, Kutch")}<br />{t("Gujarat, India")}<br /><span className="text-white/50">{t("Open daily · Brahma Muhurta to Sandhya Aarti")}</span></p>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">© {new Date().getFullYear()} Hatha Vidya Charitable Trust. {t("All rights reserved.")} · <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></div>
      </footer>
    </div>
  );
}

function MobileLink({ to, label }) {
  return <NavLink to={to} className={({ isActive }) => `block px-4 py-2.5 rounded-lg text-base font-medium ${isActive ? "text-[#194324] bg-[#E8F0EA]" : "text-[#3A4E41]"}`}>{label}</NavLink>;
}
